import { type ReactElement, useState } from 'react'

export interface PartThumbnailProps {
  /** Resolved thumbnail URL, or `null`/`undefined` when none exists — renders
   * {@link placeholder} instead. */
  readonly url: string | null | undefined
  /** CSS class for the `<img>` element — must size/fit identically to the
   * {@link placeholder}'s own box (each surface keeps its existing box
   * dimensions; only the leaf content swaps). */
  readonly imgClassName: string
  /** The surface's existing decorative placeholder element, rendered
   * unchanged when there is no URL, or once the `<img>` fails to load. */
  readonly placeholder: ReactElement
}

/**
 * Shared real-thumbnail-or-placeholder swatch (UID-411/ADR-0126 D17): a real
 * `<img>` when `url` resolves and loads, else the surface's own existing
 * placeholder, unchanged. NEVER a CSS `background-image`/`url()` — React
 * escapes the `src` attribute, so a server-supplied URL can't break out into
 * a style context, and the placeholder's own CSS mask stays a static
 * stylesheet literal, never data-driven. Mirrors `PartThumbnailSwatch` in
 * `verticals/orders/PartThumbnails.tsx`.
 *
 * `failed` is local and permanent for this URL; callers should key this
 * component on the url itself when it can change across a refetch, so a
 * later fetch that swaps in a new URL mounts a fresh instance with fresh
 * `failed` state rather than reusing one permanently stuck on a prior 404.
 */
export function PartThumbnail({ url, imgClassName, placeholder }: PartThumbnailProps) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return placeholder
  }
  return (
    <img
      className={imgClassName}
      src={url}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  )
}
